import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, concatMap, map, tap } from 'rxjs/operators';
import {
	deleteRole,
	deleteRoleError,
	deleteRoleSuccess,
	getAllRoles,
	getRole,
	getRoleError,
	getRoleSuccess,
	postRole,
	postRoleError,
	postRoleSuccess,
	updateRole,
	updateRoleError,
	updateRoleSuccess,
	getAllRoleSuccess,
	getAllRoleError,
	getFilterRole,
	getFilterRoleSuccess,
	getFilterRoleError,
} from './role.actions';
import { RoleService } from './roles.service';
import { of } from 'rxjs';
import { ToastAlert } from 'projects/access-control/src/app/Infrastructure/hooks/toast.hook';
import { TranslateService } from '@mangosoft/mango-layout';

@Injectable()
export class RoleEffects {
	dataTranslate: any;
	constructor(
		private actions$: Actions,
		private roleService: RoleService,
		private toastAlert: ToastAlert,
		private translateService: TranslateService
	) {
		this.translateService.init('AccessControl').subscribe((data) => {
			this.dataTranslate = data.pages.roles.toasts;
		});
	}

	getRole$ = createEffect(() =>
		this.actions$.pipe(
			ofType(getRole),
			concatMap(({ paginationTo }) =>
				this.roleService.getRoles(paginationTo).pipe(
					map(({ data, pagination }) => getRoleSuccess({ role: data, pagination })),
					catchError((error) => of(getRoleError({ error })))
				)
			)
		)
	);

	getFilterRole$ = createEffect(() =>
		this.actions$.pipe(
			ofType(getFilterRole),
			concatMap(({ url }) =>
				this.roleService.getFilterRoles(url).pipe(
					map(({ data, pagination }) => getFilterRoleSuccess({ role: data, pagination })),
					catchError((error) => of(getFilterRoleError({ error })))
				)
			)
		)
	);

	getAllRole$ = createEffect(() =>
		this.actions$.pipe(
			ofType(getAllRoles),
			concatMap(() =>
				this.roleService.getAllRoles().pipe(
					map(({ data: role }) => getAllRoleSuccess({ role })),
					catchError((error) => of(getAllRoleError({ error })))
				)
			)
		)
	);

	deleteRole$ = createEffect(() =>
		this.actions$.pipe(
			ofType(deleteRole),
			concatMap(({ ids }) =>
				this.roleService.deleteRole(ids).pipe(
					map(({ data }) => deleteRoleSuccess({ role: data.roles })),
					catchError((error) => of(deleteRoleError({ error })))
				)
			)
		)
	);

	postRole$ = createEffect(() =>
		this.actions$.pipe(
			ofType(postRole),
			concatMap(({ role }) =>
				this.roleService.postRole(role).pipe(
					map(({ data }) => postRoleSuccess({ role: data.role })),
					catchError((error) => of(postRoleError({ error })))
				)
			)
		)
	);

	updateRole$ = createEffect(() =>
		this.actions$.pipe(
			ofType(updateRole),
			concatMap(({ role, id }) =>
				this.roleService.updateRole(role, id).pipe(
					map((role) => updateRoleSuccess({ role })),
					catchError((error) => of(updateRoleError({ error })))
				)
			)
		)
	);

	deleteRoleSuccess$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(deleteRoleSuccess),
				tap((action) => {
					this.toastAlert.toastSuccess(
						this.dataTranslate.toastDeleteTitle,
						this.dataTranslate.toastDeleteDescription
					);
				})
			),
		{ dispatch: false }
	);

	updateRoleSuccess$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(updateRoleSuccess),
				tap((action) => {
					this.toastAlert.toastSuccess(
						this.dataTranslate.toastUpdateTitle,
						this.dataTranslate.toastUpdateDescription
					);
				})
			),
		{ dispatch: false }
	);

	postRoleSuccess$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(postRoleSuccess),
				tap((action) => {
					this.toastAlert.toastSuccess(
						this.dataTranslate.toastCreateTitle,
						this.dataTranslate.toastCreateDescription
					);
				})
			),
		{ dispatch: false }
	);

	rolesError$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(getRoleError, updateRoleError, postRoleError, deleteRoleError, getAllRoleError),
				tap(({ error }) => {
					const msgError = error.error.errorMessage || 'Internal Error ';
					this.toastAlert.toastFailure('Error', msgError);
				})
			),
		{ dispatch: false }
	);
}
